import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { API, FALLBACK_PRODUCTS } from "@/data/site";
import { useCart } from "@/context/CartContext";
import { Overline, Reveal } from "@/components/motion";

const formatINR = (n) => `₹${n.toLocaleString("en-IN")}`;

const ProductCard = ({ product, featured = false, index = 0 }) => {
  const { add, setOpen } = useCart();

  const onSpot = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  const onAdd = () => {
    add(product);
    toast.success(`${product.name} added to your caddy`, {
      action: { label: "View", onClick: () => setOpen(true) },
    });
  };

  return (
    <motion.article
      data-testid={`product-card-${product.slug}`}
      onMouseMove={onSpot}
      className={`group relative overflow-hidden border border-white/5 bg-stone-900 ${
        featured ? "md:col-span-2" : ""
      }`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className={`overflow-hidden ${featured ? "aspect-[16/9] md:aspect-[21/9]" : "aspect-[4/5]"}`}>
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110"
        />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-stone-950/95 via-stone-950/20 to-transparent" />
      <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 spotlight" />

      <span className="absolute left-5 top-5 border border-white/10 bg-stone-950/60 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-amber-500 backdrop-blur-sm">
        {product.tag}
      </span>

      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6">
        <div>
          <h3 className="font-serif text-2xl tracking-tight text-stone-100 transition-colors duration-300 group-hover:text-amber-400 md:text-3xl">
            {product.name}
          </h3>
          <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-stone-400">{product.notes}</p>
          <p className="mt-3 text-sm font-light text-stone-300">
            {formatINR(product.price)} <span className="text-stone-500">· {product.unit}</span>
          </p>
        </div>
        <button
          data-testid={`add-to-cart-${product.slug}`}
          onClick={onAdd}
          className="flex h-12 w-12 shrink-0 translate-y-2 items-center justify-center rounded-full bg-amber-600 text-stone-950 opacity-0 transition-all duration-300 hover:bg-amber-500 group-hover:translate-y-0 group-hover:opacity-100"
          aria-label={`Add ${product.name} to cart`}
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>
    </motion.article>
  );
};

const FILTERS = [
  { key: "all", label: "All Twelve" },
  { key: "classic", label: "Classic Range" },
  { key: "wellness", label: "Wellness Range" },
];

const Collection = () => {
  const [products, setProducts] = useState(FALLBACK_PRODUCTS);
  const [range, setRange] = useState("all");

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) setProducts(res.data);
      })
      .catch(() => {});
  }, []);

  const visible = range === "all" ? products : products.filter((p) => p.range === range);

  return (
    <section id="collection" data-testid="collection-section" className="relative py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-16 grid grid-cols-1 items-end gap-8 md:grid-cols-[1fr_auto]">
          <Reveal>
            <Overline>The Collection</Overline>
            <h2 className="mt-5 max-w-xl font-serif text-4xl leading-[1.05] tracking-tight text-stone-100 sm:text-5xl lg:text-6xl">
              Twelve teas, one <em className="not-italic text-amber-500">amber</em> thread
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-sm text-base font-light leading-relaxed text-stone-400 md:text-lg">
              From first-flush Darjeeling to pinewood-smoked Black Label — each lot is tasted,
              rested and sealed by hand before it earns the three-leaf mark.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mb-12">
          <div data-testid="collection-filters" className="flex flex-wrap items-center gap-3">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setRange(f.key)}
                className={`rounded-full px-6 py-2.5 text-[12px] font-medium uppercase tracking-[0.2em] transition-all duration-300 ${
                  range === f.key
                    ? "bg-amber-600 text-stone-950 shadow-[0_0_24px_rgba(217,119,6,0.3)]"
                    : "border border-white/10 text-stone-400 hover:border-amber-600/50 hover:text-amber-500"
                }`}
              >
                {f.label}
              </button>
            ))}
            <span data-testid="filter-count" className="ml-2 text-xs font-light tracking-[0.15em] text-stone-500">
              {visible.length} {visible.length === 1 ? "tea" : "teas"}
            </span>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {visible.map((p, i) => (
            <ProductCard key={p.slug} product={p} featured={range === "all" && i === 0} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Collection;
